"use client";

import Image from "next/image";

import { ArrowRightIcon } from "@/components/icons/arrowRight.icon";
import { Link } from "@/components/link/link.component";
import cn from "classnames";
import { brown } from "@/app/font";

import { resolveLinkHref } from "@/utils/url.util";

import type {
  SocialProofAward,
  SocialProofLinkBlock,
} from "./adtSocialProof.types";
import { newTabProps, socialProofEyebrowClass } from "./adtSocialProof.utils";

export function AdtSocialProofAwards({
  eyebrow,
  awards,
  link,
  disclaimer,
  showDivider = false,
}: AdtSocialProofAwardsProps) {
  const href = link?.link ? resolveLinkHref(link.link) : "#";
  const showLink = Boolean(link?.label && href !== "#");

  if (!eyebrow && !awards.length && !showLink && !disclaimer) return null;

  return (
    <div
      className="relative flex min-w-0 flex-col px-6 py-8 sm:px-8 lg:px-10 lg:py-10"
      data-testid="social-proof-awards">
      {showDivider ? (
        <span
          aria-hidden
          className="absolute inset-y-0 left-0 hidden w-px bg-white/30 lg:block"
        />
      ) : null}
      {eyebrow ? <p className={socialProofEyebrowClass}>{eyebrow}</p> : null}
      {awards.length ? (
        <ul className="mt-5 grid grid-cols-3 items-end gap-3">
          {awards.map((award) => (
            <li key={award._key} className="flex items-end justify-center">
              <Image
                src={award.url}
                alt={award.alt || "Award"}
                width={96}
                height={137}
                className="h-[120px] w-auto max-w-full object-contain object-bottom"
              />
            </li>
          ))}
        </ul>
      ) : null}
      {showLink ? (
        <Link
          href={href}
          {...newTabProps(link?.link)}
          className={cn(
            brown.className,
            "mt-6 inline-flex w-fit items-center gap-1.5 text-base font-bold text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white",
          )}>
          {link?.label}
          <ArrowRightIcon aria-hidden width={18} height={18} />
        </Link>
      ) : null}
      {disclaimer ? (
        <p className="mt-4 text-[11px] leading-snug text-white/70">
          {disclaimer}
        </p>
      ) : null}
    </div>
  );
}

type AdtSocialProofAwardsProps = {
  eyebrow?: string | null;
  awards: SocialProofAward[];
  link?: SocialProofLinkBlock | null;
  disclaimer?: string | null;
  showDivider?: boolean;
};
